import React, { useState, useEffect } from "react";
import { apiFetch, can, canAny } from "../api";
import SyncAnomaliesPanel from "../components/SyncAnomaliesPanel";
import VcaContactSkippedPanel from "../components/VcaContactSkippedPanel";
import PipelinePanel from "../components/PipelinePanel";
import AuditLogPanel from "../components/AuditLogPanel";
import ProjectStatusPanel from "../components/ProjectStatusPanel";
import TableEditor from "../components/TableEditor";
import SystemSettings from "../components/SystemSettings";

// ===================== SYSTEM ADMIN PAGE (Chunk SA-B, 2026-05-02) =====================
// Compartment for sysadmin-class surfaces: VCA Data triage, Audit Log,
// Project Status, and (Chunk SA-C, 2026-05-02) generic Table Editor.
// Page-level visibility gated by view_sysadmin_panel cap (system_admin /
// executive / ownership). Each sub-tab keeps its own cap gate so e.g.
// ownership sees a sparse page if they only have view-only access to
// certain sub-surfaces. Table Editor edit mode lands in Chunk SA-D.
export default function SystemAdmin({ onNavigateToClaim, onReconciled }) {
  const canVca           = canAny(["manage_vca_sync", "manage_pipeline"]);
  // Audit log surface is system_admin-only (gated on manage_users); the
  // server re-checks the same cap on /admin/audit-log + /admin/phi-access-log.
  const canAudit         = can("manage_users");
  // Project Status — gated on view_sysadmin_panel (relocated from
  // view_project_status in Chunk SA-B).  Read-only surface, no mutating actions.
  const canProjectStatus = can("view_sysadmin_panel");
  // Table Editor — also gated on view_sysadmin_panel (Chunk SA-C).
  const canTableEditor   = can("view_sysadmin_panel");
  // System Settings — also gated on view_sysadmin_panel (Chunk SS-4).
  const canSystemSettings = can("view_sysadmin_panel");

  const firstVisibleTab  = canVca ? "vca"
                         : canAudit ? "audit"
                         : canProjectStatus ? "project_status"
                         : canTableEditor ? "table_editor"
                         : canSystemSettings ? "system_settings"
                         : "project_status";  // fallback — page-level gate prevents reach
  const [tab, setTab] = useState(firstVisibleTab);
  const [anomalyCount, setAnomalyCount] = useState(null); // VCA Data tab red bubble (Chunk SG3 + RA)

  // VCA Data tab red bubble.  Counts unacknowledged sync anomalies AND
  // adds 1 if the most recent reconcile run errored.  Two distinct
  // signals folded into one badge so a manager glancing at the tab
  // sees "something's wrong over there" without needing to disambiguate.
  // Chunk RA (Reconcile Alerting) widened this from anomaly-only.
  const fetchAnomalyCount = () => {
    if (!canVca) { setAnomalyCount(0); return; }
    Promise.allSettled([
      apiFetch("/admin/sync-anomalies/unacknowledged-count"),
      apiFetch("/admin/project-status/last-reconcile-status"),
    ]).then(([a, r]) => {
      const anom = a.status === "fulfilled" ? (a.value?.count ?? 0) : 0;
      const reconcileFailed =
        r.status === "fulfilled" &&
        (r.value?.status === "error" || (r.value?.status === "success" && r.value?.is_stale));
      setAnomalyCount(anom + (reconcileFailed ? 1 : 0));
    });
  };
  useEffect(() => { fetchAnomalyCount(); }, [tab]);

  return (
    <div className="page">
      <div className="tabs">
        {canVca && (
          <button className={`tab-btn ${tab==="vca"?"active":""}`} onClick={() => setTab("vca")}>
            VCA Data
            {anomalyCount > 0 && (
              <span style={{marginLeft:6,padding:"1px 7px",borderRadius:10,background:"#dc2626",color:"#fff",fontSize:11,fontWeight:600}}>
                {anomalyCount}
              </span>
            )}
          </button>
        )}
        {canAudit && (
          <button className={`tab-btn ${tab==="audit"?"active":""}`} onClick={() => setTab("audit")}>
            Audit Log
          </button>
        )}
        {canProjectStatus && (
          <button className={`tab-btn ${tab==="project_status"?"active":""}`} onClick={() => setTab("project_status")}>
            Project Status
          </button>
        )}
        {canTableEditor && (
          <button className={`tab-btn ${tab==="table_editor"?"active":""}`} onClick={() => setTab("table_editor")}>
            Table Editor
          </button>
        )}
        {canSystemSettings && (
          <button className={`tab-btn ${tab==="system_settings"?"active":""}`} onClick={() => setTab("system_settings")}>
            System Settings
          </button>
        )}
      </div>
      {tab === "vca" && canVca && (
        <>
          <SyncAnomaliesPanel onChanged={fetchAnomalyCount}/>
          <VcaContactSkippedPanel/>
          <PipelinePanel onReconciled={onReconciled}/>
        </>
      )}
      {tab === "audit" && canAudit && <AuditLogPanel onNavigateToClaim={onNavigateToClaim}/>}
      {tab === "project_status" && canProjectStatus && <ProjectStatusPanel/>}
      {tab === "table_editor" && canTableEditor && <TableEditor/>}
      {tab === "system_settings" && canSystemSettings && <SystemSettings/>}
    </div>
  );
}
